import { createSlice } from '@reduxjs/toolkit';
import { viewCourseAdapter } from './hooks/useViewCourseState';
import { generateCourseStructure } from '../../utils/generateCourseStructure';

// ViewCourse state slice - only state management
// All selectors moved to hooks/useViewCourseState.js

const initialState = {
  tocMenuIsClosed: false,
  currentCourseId: null,
  currentSectionId: null,
  currentPageId: null,
  nextAvailablePageId: null,
  dripContent: null,
  sections: {
    ids: [],
    entities: {}
  },
  pages: {
    ids: [],
    entities: {}
  },
};

const courseViewSlice = createSlice({
  name: 'viewCourse',
  initialState,
  reducers: {
    reset: (state) => {
      return initialState;
    },
    toggleTocMenuIsClosed: (state) => {
      state.tocMenuIsClosed = !state.tocMenuIsClosed;
    },
    setCurrentPageId: (state, action) => {
      state.currentPageId = action.payload;
    },
    setActiveSectionId: (state, action) => {
      state.currentSectionId = action.payload;
    },
    initializeCourseStructure: (state, action) => {
      const course = action.payload;
      const dripContent = course.settings?.dripContent || {};
      const isGated = dripContent.gatedAccess === true;

      // Safety check: if this is a different course, reset currentPageId
      const isDifferentCourse = state.currentCourseId && state.currentCourseId !== course.id;
      const initialPageId = isDifferentCourse ? course.lastViewedPageId : (state.currentPageId || course.lastViewedPageId);
      const { sections, pages, nextAvailablePageId, currentSectionId, lastUnlockedPageId } = generateCourseStructure({
        toc: course.toc,
        isGated,
        initialPageId,
      });

      const firstContentPage = Array.isArray(pages) ? pages.find(p => p && p.id && p.id !== 'diploma') : null;
      const resolvedPageId = initialPageId || lastUnlockedPageId || nextAvailablePageId || firstContentPage?.id || null;
      const resolvedSectionId = currentSectionId || (Array.isArray(pages) ? (pages.find(p => p.id === resolvedPageId)?.sectionId ?? null) : null);

      state.currentCourseId = course.id;
      state.currentPageId = resolvedPageId;
      state.currentSectionId = resolvedSectionId;
      state.nextAvailablePageId = nextAvailablePageId;
      state.lastUnlockedPageId = lastUnlockedPageId;
      state.dripContent = dripContent;

      viewCourseAdapter.setAll(state.sections, sections);
      viewCourseAdapter.setAll(state.pages, pages);
    },
    updateCourseProgress: (state, action) => {
      const isGated = state.dripContent?.gatedAccess === true;
      const progressData = action.payload.hasOwnProperty('toc') ? action.payload.toc : action.payload;

      const progressMap = {};
      progressData.forEach(section => {
        section.pages.forEach(p => {
          progressMap[p.id] = p.progress ?? 0;
        });
      });

      const fullToc = state.sections.ids.map(id => {
        const section = state.sections.entities[id];
        const pageIds = Array.isArray(section.pages)
          ? section.pages
          : Object.values(section.pages);

        const pageObjs = pageIds
          .map((pageId) => state.pages.entities[pageId])
          .filter(Boolean)
          .map((page) => JSON.parse(JSON.stringify(page)));

        return { ...section, pages: pageObjs };
      });

      const { sections, pages, nextAvailablePageId, lastUnlockedPageId } = generateCourseStructure({
        toc: fullToc,
        isGated,
        progressMap,
      });

      viewCourseAdapter.upsertMany(state.sections, sections);
      viewCourseAdapter.upsertMany(state.pages, pages);
      state.nextAvailablePageId = nextAvailablePageId;
      state.lastUnlockedPageId = lastUnlockedPageId;
    },
  }
});

export const { 
  toggleTocMenuIsClosed, 
  setCurrentPageId, 
  reset, 
  setActiveSectionId, 
  initializeCourseStructure, 
  updateCourseProgress 
} = courseViewSlice.actions;

export default courseViewSlice.reducer;
